import type { FeatureCollection, Polygon } from 'geojson';
import { ApiModule } from '@core';
import type { ParcelGroupItem, SaveParcelGroup } from '../models/parcel-group.model';
import type { OfferItem } from '../models/offer-item.model';
import type { ParcelData } from '../models/parcel-details.model';
import type { ParcelItem } from '../models/parcel-item.model';
import type { GeoLayerPreviewData } from '../../geo-layers-upload/models';
import type { LayerGroupClassification, Permission } from '@shared';
import type { ParcelStat } from '../models/parcel-stat.model';
import type { ParcelListFilter } from '../models/parcel-list-filter.model';

export class ParcelApi extends ApiModule {
  protected endpoint = '/maximap';

  public getParcelGroups(permission: Permission, year?: number, filter?: ParcelListFilter) {
    return this.api.get<ParcelGroupItem[]>(`${this.endpoint}/parcel-groups`, {
      params: { permission, year, ...filter }
    });
  }

  public getParcelGroup(id: string) {
    return this.api.get<ParcelGroupItem[]>(`${this.endpoint}/parcel-groups/${id}`);
  }

  public getOffers(permission: Permission, year?: number, filter?: ParcelListFilter) {
    return this.api.get<OfferItem[]>(`${this.endpoint}/offer-groups`, {
      params: { permission, year, ...filter }
    });
  }

  public getDemoParcels(year?: number, filter?: ParcelListFilter) {
    return this.api.get<ParcelItem[]>(`${this.endpoint}/demo-parcels`, { params: { year, ...filter } });
  }

  public createParcelGroup(saveParcelGroup: SaveParcelGroup) {
    return this.api.post<ParcelGroupItem, SaveParcelGroup>(`${this.endpoint}/parcel-groups`, saveParcelGroup);
  }

  public getParcelData(parcelId: string, classification: LayerGroupClassification[]) {
    return this.api.get<ParcelData>(`${this.endpoint}/layers/${parcelId}`, { params: { classification } });
  }

  public getLayer(layerId: string) {
    return this.api.get<any>(`${this.endpoint}/layer/${layerId}`);
  }

  public editLayer(layerId: string, dataToUpdate: any) {
    return this.api.patch<any, any>(`${this.endpoint}/layer-group/${layerId}`, dataToUpdate);
  }

  public removeLayer(layerId: string) {
    return this.api.delete<void>(`${this.endpoint}/layer-group/${layerId}`);
  }

  public downloadLayer(layerId: string) {
    return this.api.get<any>(`${this.endpoint}/layer-group/${layerId}/download`, {
      responseType: 'blob'
    });
  }

  public editParcelGroup(id: string, saveParcelGroup: SaveParcelGroup) {
    return this.api.put<ParcelGroupItem, SaveParcelGroup>(`${this.endpoint}/parcel-groups/${id}`, saveParcelGroup);
  }

  public deleteParcelGroup(id: string) {
    return this.api.delete<void>(`${this.endpoint}/parcel-groups/${id}`);
  }

  public getRasterFile(groupId: string) {
    return this.api.get<any>(`${this.endpoint}/raster-file/${groupId}`, {
      responseType: 'blob'
    });
  }

  public getParcelGeometry(parcelId: string) {
    return this.api.get<FeatureCollection<Polygon, GeoLayerPreviewData>>('/parcel/geometry', {
      params: { parcels: [parcelId] }
    });
  }

  public getParcelsOutline(parcels: string[]) {
    return this.api.get<any>(`${this.endpoint}/outline`, { params: { parcels } });
  }

  public loadParcelStat() {
    return this.api.get<ParcelStat>(`parcel/stat`);
  }
}
